<template>
  <div ref="formRoot" class="ad-form">
    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:domain" width="16" color="#888" />
        <span>Company</span>
        <div v-if="sectionDocNames.length" class="ad-section-docs">
          <span class="ad-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in sectionDocNames.slice(0, MAX_DOC_CHIPS)"
            :key="'company-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="ad-doc-chip"
          >
            {{ name }}
          </v-chip>
          <span v-if="sectionDocNames.length > MAX_DOC_CHIPS" class="ad-more-chip">
            +{{ sectionDocNames.length - MAX_DOC_CHIPS }}
          </span>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="8">
          <v-text-field
            v-model="model.companyName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('companyName')"
            data-required-key="companyName"
            class="ad-required-field"
          >
            <template #label>Company name <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="model.companyRegistrationNo"
            variant="outlined"
            density="compact"
            hide-details="auto"
            readonly
            :error="hasRequiredError('companyRegistrationNo')"
            data-required-key="companyRegistrationNo"
            class="ad-required-field"
          >
            <template #label>UEN <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:file-certificate-outline" width="16" color="#888" />
        <span>Board declaration</span>
        <div v-if="sectionDocNames.length" class="ad-section-docs">
          <span class="ad-section-docs-label">Applies to:</span>
          <v-chip
            v-for="(name, i) in sectionDocNames.slice(0, MAX_DOC_CHIPS)"
            :key="'decl-doc-' + i"
            size="x-small"
            variant="tonal"
            color="primary"
            class="ad-doc-chip"
          >
            {{ name }}
          </v-chip>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-menu
            v-model="confirmationDateMenu"
            :close-on-content-click="false"
            location="bottom"
            :disabled="!!readOnly"
          >
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                v-model="confirmationDateDisplay"
                :placeholder="confirmationDatePlaceholder"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                append-inner-icon="mdi-calendar"
                :disabled="!!readOnly"
                :error="hasRequiredError('confirmationDate')"
                data-required-key="confirmationDate"
                class="ad-required-field"
              >
                <template #label>Confirmation date <span class="ad-required-mark">*</span></template>
              </v-text-field>
            </template>
            <v-date-picker
              v-bind="confirmationDatePickerProps"
              v-model="confirmationDatePickerValue"
              @update:model-value="confirmationDateMenu = false"
            />
          </v-menu>
          <v-chip
            v-if="shouldShowDateSuggestion('confirmationDate', suggestedToday)"
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 ad-suggest-chip"
            @mousedown.stop.prevent
            @click.stop.prevent="applySuggestedDate('confirmationDate', suggestedToday)"
            @click:close.stop.prevent="dismissSuggestion('confirmationDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
      </v-row>
      <p class="ad-hint-text">
        Date on which the board confirms the registrable controllers listed below for ACRA registration.
      </p>
    </div>

    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:account-group-outline" width="16" color="#888" />
        <span>Registrable controllers to confirm</span>
        <v-chip v-if="model.controllers.length" size="x-small" variant="tonal" color="primary" class="ml-2">
          {{ selectedCount }} / {{ model.controllers.length }} selected
        </v-chip>
      </div>

      <div
        class="ad-controllers-loop-block"
        :class="{ 'ad-controllers-loop-block--error': hasRequiredError('controllers') }"
        data-required-key="controllers"
      >
        <div v-if="!readOnly" class="d-flex justify-end align-center pb-1 ga-1 flex-wrap">
          <v-btn size="x-small" variant="text" color="primary" @click="addControllerRow">
            <v-icon start size="14">mdi-plus</v-icon>
            Add controller
          </v-btn>
          <v-btn
            v-if="model.controllers.length"
            size="x-small"
            variant="text"
            color="primary"
            @click="selectAll(true)"
          >
            Select all
          </v-btn>
          <v-btn
            v-if="model.controllers.length"
            size="x-small"
            variant="text"
            color="grey"
            @click="selectAll(false)"
          >
            Clear
          </v-btn>
          <v-btn
            size="x-small"
            variant="text"
            color="primary"
            :loading="loadingControllers"
            @click="reloadControllers"
          >
            <v-icon start size="14">mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </div>

        <v-alert
          v-if="loadingControllers"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-2"
        >
          Loading controllers from this entity…
        </v-alert>

        <div v-else-if="!model.controllers.length" class="ad-loop-empty">
          No controllers yet. Click <strong>Add controller</strong> or <strong>Refresh</strong> to load from the entity.
        </div>

        <div
          v-for="(row, idx) in model.controllers"
          :key="rowKey(row, idx)"
          class="ad-loop-row"
          :class="{ 'ad-loop-row--selected': row.selected }"
        >
          <v-row dense align="center">
            <v-col v-if="!readOnly" cols="auto" class="d-flex align-center pt-1">
              <v-checkbox-btn
                v-model="row.selected"
                density="compact"
                hide-details
                color="primary"
              />
            </v-col>
            <v-col cols="12" :md="readOnly ? 12 : 6">
              <v-text-field
                v-model="row.fullName"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :readonly="!!readOnly"
                :error="hasRequiredError(`controllers.${idx}.fullName`)"
                :data-required-key="`controllers.${idx}.fullName`"
                :class="{ 'ad-required-field': row.selected }"
              >
                <template #label>
                  Controller name
                  <span v-if="row.selected && !readOnly" class="ad-required-mark">*</span>
                </template>
                <template v-if="!readOnly" #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn
                        v-bind="tProps"
                        icon="mdi-creation"
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click.stop="requestControllerLookup(idx)"
                      />
                    </template>
                    <span>Lookup controller from chat</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div v-if="controllerMetaLine(row) !== '—'" class="text-caption text-medium-emphasis ad-controller-meta-line">
                {{ controllerMetaLine(row) }}
              </div>
            </v-col>
            <v-col v-if="!readOnly" cols="auto" class="d-flex align-center justify-center pa-0">
              <v-btn
                variant="text"
                size="small"
                icon
                :disabled="readOnly"
                @click="removeControllerRow(idx)"
              >
                <v-icon size="16">mdi-close</v-icon>
              </v-btn>
            </v-col>
            <v-col v-else-if="readOnly" cols="auto" class="d-flex align-center">
              <v-icon v-if="row.selected" size="20" color="primary">mdi-check-circle</v-icon>
            </v-col>
          </v-row>
        </div>

        <div v-if="hasRequiredError('controllers')" class="ad-required-helper">
          Select at least one controller with a name.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import {
  getCurrentDateByJurisdiction,
  useDateField,
  useFormatDateByUserFormat,
} from '@/shared/composables/dateUtils';
import {
  ensureB15JurisdictionNameMap,
  resolveB15CodeToName,
} from '../notice-corporate/composables/b15JurisdictionDisplay';
import { fetchActiveEntityControllers } from './composables/entityControllersApi';
import {
  createEmptyControllerSelection,
  isManualControllerRow,
  mapOfficerToControllerSelection,
  selectionsFromEntityRows,
  type ConfirmationControllerSelection,
  type ConfirmationControllersFormModel,
} from './composables/useFormPayload';

const MAX_DOC_CHIPS = 3;
const DEFAULT_DOC = 'Confirmation of Registrable Controllers.docx';

const props = withDefaults(
  defineProps<{
    model: ConfirmationControllersFormModel;
    entityCd?: string;
    readOnly?: boolean;
    documents?: Array<{ templateName?: string; docCode?: string }>;
    selectedPerson?: OfficerDetail | null;
    activePersonContext?: Record<string, unknown> | null;
  }>(),
  {
    entityCd: '',
    readOnly: false,
    documents: () => [],
    selectedPerson: null,
    activePersonContext: null,
  },
);

const emit = defineEmits<{
  (e: 'request-person-lookup', context: Record<string, unknown>): void;
}>();

const formRoot = ref<HTMLElement | null>(null);
const requiredErrorKeys = ref<Set<string>>(new Set());
const loadingControllers = ref(false);
const b15Map = ref(new Map<string, string>());

const formatDateByUserFormat = useFormatDateByUserFormat();
const suggestedToday = computed(() => getCurrentDateByJurisdiction());
const dismissedSuggestions = ref<Set<string>>(new Set());

const sectionDocNames = computed(() => {
  const names = (props.documents || [])
    .map((d) => trim(d.templateName))
    .filter(Boolean);
  return names.length ? names : [DEFAULT_DOC];
});

const selectedCount = computed(
  () => props.model.controllers.filter((c) => c.selected).length,
);

function hasRequiredError(key: string): boolean {
  return requiredErrorKeys.value.has(key);
}

function setRequiredErrors(keys: string[]) {
  requiredErrorKeys.value = new Set(keys);
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function firstMissingRequiredKeys(): string[] {
  const missing: string[] = [];
  const pushIfEmpty = (key: string, value: unknown) => {
    if (!trim(value)) missing.push(key);
  };

  pushIfEmpty('companyName', props.model.companyName);
  pushIfEmpty('companyRegistrationNo', props.model.companyRegistrationNo);
  pushIfEmpty('confirmationDate', props.model.confirmationDate);
  if (!props.model.controllers.some((c) => c.selected)) {
    missing.push('controllers');
  }
  props.model.controllers.forEach((c, idx) => {
    if (c.selected && !trim(c.fullName)) {
      missing.push(`controllers.${idx}.fullName`);
    }
  });

  return missing;
}

async function focusRequiredField(key: string): Promise<void> {
  await nextTick();
  const root = formRoot.value;
  if (!root) return;
  const target = root.querySelector<HTMLElement>(`[data-required-key="${key}"]`);
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable = target.querySelector<HTMLElement>('input, textarea, [role="combobox"], button');
  focusable?.focus();
}

async function validateAndFocusRequiredFields(): Promise<boolean> {
  const missingKeys = firstMissingRequiredKeys();
  setRequiredErrors(missingKeys);
  if (missingKeys.length === 0) return true;
  await focusRequiredField(missingKeys[0]);
  return false;
}

defineExpose({
  validateAndFocusRequiredFields,
});

function categoryLabel(cd: string): string {
  const c = trim(cd).toUpperCase();
  if (c === 'B09001') return 'Corporate';
  if (c === 'B09002') return 'Individual';
  return c || '—';
}

function jurisdictionDisplay(cd: string): string {
  return resolveB15CodeToName(cd, b15Map.value) || cd || '';
}

function formatDisplayDate(raw: string): string {
  const s = trim(raw);
  if (!s) return '';
  const formatted = formatDateByUserFormat(s);
  return formatted?.trim() ? formatted : s;
}

function selectAll(on: boolean) {
  for (const c of props.model.controllers) {
    c.selected = on;
  }
}

function regNoDisplay(row: ConfirmationControllerSelection): string {
  return trim(row.contactRegNo || row.regNo || row.nonRegNo) || '—';
}

function controllerMetaLine(row: ConfirmationControllerSelection): string {
  const parts: string[] = [];
  const cat = categoryLabel(row.categoryCd);
  if (cat && cat !== '—') parts.push(cat);
  const jur = jurisdictionDisplay(row.jurisdictionCd);
  if (jur) parts.push(jur);
  const reg = regNoDisplay(row);
  if (reg !== '—') parts.push(reg);
  if (row.becomingDate) {
    parts.push(`Became ${formatDisplayDate(row.becomingDate)}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

function rowKey(row: ConfirmationControllerSelection, idx: number): string {
  return trim(row._localKey) || trim(row.ctrlIdx) || `row-${idx}`;
}

function addControllerRow() {
  if (props.readOnly) return;
  props.model.controllers.push(createEmptyControllerSelection(true));
}

function removeControllerRow(idx: number) {
  if (props.readOnly) return;
  props.model.controllers.splice(idx, 1);
}

function requestControllerLookup(index: number) {
  if (props.readOnly) return;
  emit('request-person-lookup', {
    fieldGroup: 'confirmationController',
    index,
    defaultRole: 'CONTROLLER',
  });
}

function applyPersonToRow(row: ConfirmationControllerSelection, person: OfficerDetail) {
  const mapped = mapOfficerToControllerSelection(
    person as unknown as Record<string, unknown>,
    true,
  );
  row.ctrlIdx = mapped.ctrlIdx || row.ctrlIdx;
  row.referencesContactCd = mapped.referencesContactCd || row.referencesContactCd;
  if (mapped.fullName) row.fullName = mapped.fullName;
  if (mapped.categoryCd) row.categoryCd = mapped.categoryCd;
  if (mapped.legalForm) row.legalForm = mapped.legalForm;
  if (mapped.jurisdictionCd) row.jurisdictionCd = mapped.jurisdictionCd;
  if (mapped.incorporationStatute) row.incorporationStatute = mapped.incorporationStatute;
  if (mapped.corporateEntityRegister) row.corporateEntityRegister = mapped.corporateEntityRegister;
  if (mapped.contactRegNo) row.contactRegNo = mapped.contactRegNo;
  if (mapped.regNo) row.regNo = mapped.regNo;
  if (mapped.nonRegNo) row.nonRegNo = mapped.nonRegNo;
  if (mapped.becomingDate) row.becomingDate = mapped.becomingDate;
  row.selected = true;
}

function findRowIndexByContactCd(contactCd: string): number {
  const cd = trim(contactCd).toUpperCase();
  if (!cd) return -1;
  return props.model.controllers.findIndex(
    (r) => trim(r.referencesContactCd).toUpperCase() === cd,
  );
}

async function reloadControllers() {
  const entityCd = trim(props.entityCd);
  if (!entityCd) return;
  loadingControllers.value = true;
  try {
    if (!b15Map.value.size) {
      b15Map.value = await ensureB15JurisdictionNameMap();
    }
    const rows = await fetchActiveEntityControllers(entityCd);
    const manualRows = props.model.controllers.filter((c) => isManualControllerRow(c));
    const prevSelected = new Map(
      props.model.controllers.map((c) => [trim(c.ctrlIdx) || trim(c._localKey), c.selected]),
    );
    const entityRows = selectionsFromEntityRows(rows, true).map((c) => {
      const key = trim(c.ctrlIdx);
      return {
        ...c,
        selected: prevSelected.has(key) ? !!prevSelected.get(key) : true,
      };
    });
    props.model.controllers = [...entityRows, ...manualRows];
  } finally {
    loadingControllers.value = false;
  }
}

function normalizeIso(v: unknown): string {
  return trim(v);
}

function bindModelDate(
  get: () => string,
  set: (v: string) => void,
  field: ReturnType<typeof useDateField>,
) {
  watch(get, (v) => {
    const s = normalizeIso(v);
    if (field.dateValue.value !== s) field.dateValue.value = s;
  }, { immediate: true });
  watch(field.dateValue, (v) => {
    const s = normalizeIso(v);
    if (normalizeIso(get()) !== s) set(s);
  });
}

const confirmationDateField = useDateField();
const confirmationDateMenu = ref(false);
bindModelDate(
  () => props.model.confirmationDate,
  (v) => { props.model.confirmationDate = v; },
  confirmationDateField,
);
const confirmationDateDisplay = confirmationDateField.dateDisplay;
const confirmationDatePlaceholder = confirmationDateField.datePlaceholder;
const confirmationDatePickerValue = confirmationDateField.datePickerValue;
const confirmationDatePickerProps = confirmationDateField.datePickerProps;

function formatIsoForSession(iso: string): string {
  return formatDateByUserFormat(iso) || iso;
}

function shouldShowDateSuggestion(field: string, iso: string): boolean {
  if (props.readOnly) return false;
  const key = `${field}:${iso}`;
  if (dismissedSuggestions.value.has(key)) return false;
  return !normalizeIso(props.model.confirmationDate);
}

function applySuggestedDate(field: 'confirmationDate', iso: string) {
  if (field === 'confirmationDate') props.model.confirmationDate = iso;
  dismissedSuggestions.value.add(`${field}:${iso}`);
}

function dismissSuggestion(field: string, iso: string) {
  dismissedSuggestions.value.add(`${field}:${iso}`);
}

watch(
  () => props.selectedPerson,
  (person) => {
    if (!person || props.readOnly) return;
    const ctx = props.activePersonContext;
    if (String(ctx?.fieldGroup || '') !== 'confirmationController') return;
    const index = typeof ctx?.index === 'number' ? ctx.index : -1;
    const raw = person as unknown as Record<string, unknown>;
    const contactCd = trim(person.contactCd ?? raw.contactCd);

    if (index >= 0 && index < props.model.controllers.length) {
      applyPersonToRow(props.model.controllers[index], person);
      return;
    }

    const existingIdx = findRowIndexByContactCd(contactCd);
    if (existingIdx >= 0) {
      applyPersonToRow(props.model.controllers[existingIdx], person);
      return;
    }

    const row = createEmptyControllerSelection(true);
    applyPersonToRow(row, person);
    props.model.controllers.push(row);
  },
);

watch(
  () => props.entityCd,
  (ec) => {
    if (ec) void reloadControllers();
  },
  { immediate: true },
);

watch(
  () => props.model,
  () => {
    if (requiredErrorKeys.value.size === 0) return;
    setRequiredErrors(firstMissingRequiredKeys());
  },
  { deep: true },
);
</script>

<style scoped>
.ad-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ad-section {
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e9edf5;
  border-radius: 12px;
  padding: 0 0 12px 0;
  overflow: hidden;
}

.ad-section > :not(.ad-section-header) {
  padding-left: 14px;
  padding-right: 14px;
}

.ad-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #283593;
  margin: 0 0 12px 0;
  padding: 10px 14px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-bottom: 1px solid #d6dcf2;
}

.ad-section-header--split {
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
}

.ad-section-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.ad-section-docs {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  flex-wrap: wrap;
}

.ad-section-docs-label,
.ad-more-chip {
  font-size: 11px;
  color: #888;
}

.ad-doc-chip {
  max-width: 240px;
}

.ad-doc-chip :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ad-hint-text {
  font-size: 11px;
  color: #666;
  line-height: 1.45;
  margin: 8px 0 0;
}

.ad-hint-text code {
  font-size: 10px;
  background: #f5f5f5;
  padding: 1px 4px;
  border-radius: 3px;
}

.ad-controllers-loop-block {
  margin-bottom: 4px;
}

.ad-controllers-loop-block--error {
  outline: 1px solid #b3261e;
  border-radius: 8px;
  padding: 4px 6px;
}

.ad-loop-row {
  padding: 6px 0;
  border-bottom: 1px dashed #f0f0f0;
}

.ad-loop-row:last-child {
  border-bottom: none;
}

.ad-loop-row--selected {
  background: #fafbff;
  border-radius: 8px;
  padding-left: 4px;
  padding-right: 4px;
}

.ad-loop-empty {
  font-size: 12px;
  color: #aaa;
  padding: 4px 0 10px;
  font-style: italic;
}

.ad-controller-meta-line {
  margin: 2px 0 0 2px;
  line-height: 1.4;
}

.ad-required-helper {
  font-size: 12px;
  color: #b3261e;
  margin-top: 6px;
}

.ad-suggest-chip {
  cursor: pointer;
  font-size: 11px !important;
  background: #ffffff !important;
  color: #212121 !important;
  border-color: #ffb300 !important;
  border-width: 1px !important;
}

.ad-suggest-chip:hover {
  background: #fff8e1 !important;
}

.ad-suggest-chip :deep(.v-chip__content) {
  color: #212121 !important;
}

.ad-suggest-chip :deep(.v-icon) {
  color: #ffb300 !important;
}

.ad-required-mark {
  color: #b3261e;
}

:deep(.ad-required-field.v-input--error .v-label) {
  color: #b3261e !important;
}
</style>
